import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QuickClientForm } from '@/features/client-management/components/QuickClientForm';
import { useAgencyClients } from '@/features/visits/hooks/useAgencyClients';
import { SearchAutocomplete, Text, UnstyledButton } from '@/shared/ui';

export interface OwnerClientPickerProps {
  agencyId: string;
  value: string | null;
  onChange: (clientId: string | null) => void;
  /** Marks the label required, as when the agency requires an owner. */
  required?: boolean;
  error?: string;
}

/**
 * Returns whether any option label/sublabel matches the query.
 *
 * @param options - Autocomplete options.
 * @param query - User search text.
 * @returns True when at least one option matches.
 */
function hasMatchingOption(
  options: Array<{ label: string; sublabel?: string }>,
  query: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return options.length > 0;
  return options.some(
    (o) =>
      o.label.toLowerCase().includes(q) ||
      (o.sublabel?.toLowerCase().includes(q) ?? false),
  );
}

/**
 * Owner client search with inline quick-create for the property form.
 *
 * @param props - Agency scope and controlled owner id.
 * @returns Picker or quick-create form.
 */
export function OwnerClientPicker({ agencyId, value, onChange, required = false, error }: OwnerClientPickerProps) {
  const { t } = useTranslation('features/properties');
  const { clients, isLoading, reload } = useAgencyClients(agencyId);
  const [query, setQuery] = useState('');
  const [showQuick, setShowQuick] = useState(false);
  const options = useMemo(
    () => clients.map((c) => ({ id: c.id, label: c.label, sublabel: c.sublabel })),
    [clients],
  );
  const showEmptyCta =
    !showQuick && query.trim().length > 0 && !hasMatchingOption(options, query);

  if (showQuick) {
    return (
      <QuickClientForm
        agencyId={agencyId}
        onCancel={() => setShowQuick(false)}
        onCreated={(id) => {
          onChange(id);
          setShowQuick(false);
          setQuery('');
          void reload();
        }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-(--space-2)" data-testid="owner-client-picker" data-field="owner_client_id">
      <div className="flex items-center justify-between gap-(--space-2)">
        <Text
          component="span"
          className="text-[length:var(--text-small)] font-semibold text-(--text-primary)"
        >
          {t('propertyDrawer.fields.ownerClientId')}
          {required && <Text component="span" className="ml-0.5 text-(--status-error)">*</Text>}
        </Text>
        <UnstyledButton
          type="button"
          data-testid="owner-new-client"
          onClick={() => setShowQuick(true)}
          className="flex items-center gap-(--space-1) text-[length:var(--text-meta)] font-medium text-(--seed-base) underline-offset-2 hover:underline"
        >
          + {t('propertyDrawer.fields.newClient')}
        </UnstyledButton>
      </div>
      <SearchAutocomplete
        placeholder={
          isLoading
            ? t('common:labels.loading', 'Loading...')
            : t('propertyDrawer.fields.ownerClientIdPlaceholder')
        }
        options={options}
        value={value}
        onChange={onChange}
        onQueryChange={setQuery}
        emptyLabel={t('propertyDrawer.fields.noClientsFound')}
      />
      {error ? (
        <Text className="text-[length:var(--text-meta)] text-(--status-error)">{error}</Text>
      ) : null}
      {showEmptyCta ? (
        <Text className="text-[length:var(--text-meta)] text-(--text-tertiary)">
          {t('propertyDrawer.fields.noClientsFound')}{' '}
          <UnstyledButton
            type="button"
            data-testid="owner-create-client-cta"
            onClick={() => setShowQuick(true)}
            className="font-medium text-(--seed-base) underline-offset-2 hover:underline"
          >
            {t('propertyDrawer.fields.newClient')}
          </UnstyledButton>
        </Text>
      ) : null}
    </div>
  );
}
